'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { ButtonProps } from '@/ui-components/atoms/Button/Button';
import useInheritParams from '@/hooks/useInheritParams';
import { URL_INHERIT_PARAMS } from '@/constants';
import type { OfferChannels, PlanCardContent } from './PlanCard.types';
import { resolvePlanCardOfferSelection, withInheritedHref } from './resolvePlanCardOfferSelection';

interface PlanCardSelection {
    switches: Record<string, boolean>;
    selectedItems: Record<string, string>;
    priceText: ReactNode;
    selectedCta: ButtonProps | null;
    selectedOfferChannels: OfferChannels | null;
}

interface UsePlanCardSelectionOptions {
    planCardContent: PlanCardContent;
    allAccessPriceText?: ReactNode;
}

export function usePlanCardSelection({ planCardContent, allAccessPriceText }: UsePlanCardSelectionOptions) {
    const inheritedPlanCtaHref = useInheritParams(planCardContent.cta?.href || '', URL_INHERIT_PARAMS);
    const inheritedAllSelectedHref = useInheritParams(
        planCardContent.buildPlan?.all_selected_offer_cta?.href || '',
        URL_INHERIT_PARAMS,
    );
    const inheritedHrefs = {
        base: inheritedPlanCtaHref,
        allSelected: inheritedAllSelectedHref,
        offer1: useInheritParams(planCardContent.buildPlan?.offer1Cta?.href || '', URL_INHERIT_PARAMS),
        offer2: useInheritParams(planCardContent.buildPlan?.offer2Cta?.href || '', URL_INHERIT_PARAMS),
        offer3: useInheritParams(planCardContent.buildPlan?.offer3Cta?.href || '', URL_INHERIT_PARAMS),
        offer12: useInheritParams(planCardContent.buildPlan?.offer12Cta?.href || '', URL_INHERIT_PARAMS),
        offer13: useInheritParams(planCardContent.buildPlan?.offer13Cta?.href || '', URL_INHERIT_PARAMS),
        offer23: useInheritParams(planCardContent.buildPlan?.offer23Cta?.href || '', URL_INHERIT_PARAMS),
    };

    const baseSelection = (): PlanCardSelection => ({
        switches: {},
        selectedItems: {},
        priceText: planCardContent.planPriceText,
        selectedCta: withInheritedHref(planCardContent.cta, inheritedPlanCtaHref),
        selectedOfferChannels: planCardContent.planCardChannels,
    });
    const [selection, setSelection] = useState(baseSelection);
    const [allAccessModalOpen, setAllAccessModalOpen] = useState(false);
    const [startAnimation, setStartAnimation] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);
    const [fadeTiles, setFadeTiles] = useState(false);
    const toggleTimersRef = useRef(new Set<ReturnType<typeof setTimeout>>());

    useEffect(() => {
        const timers = toggleTimersRef.current;
        return () => {
            timers.forEach(clearTimeout);
            timers.clear();
        };
    }, []);

    const toggleSwitch = (key: string, addOnName: string) => {
        setFadeTiles(true);
        const timer = setTimeout(() => {
            toggleTimersRef.current.delete(timer);
            // Commit the switches and their resolved content together, using the latest selection.
            setSelection((previous) => {
                const isCurrentlyOn = !!previous.switches[key];
                const switches = { ...previous.switches, [key]: !isCurrentlyOn };
                const selectedItems = { ...previous.selectedItems };
                if (isCurrentlyOn) {
                    delete selectedItems[key];
                } else {
                    selectedItems[key] = addOnName;
                }
                const offerSelection = resolvePlanCardOfferSelection({
                    switches,
                    planCardContent,
                    allAccessPriceText,
                    previousCta: previous.selectedCta,
                    inheritedHrefs,
                });
                return {
                    switches,
                    selectedItems,
                    priceText: offerSelection.priceText,
                    selectedCta: offerSelection.cta,
                    selectedOfferChannels: offerSelection.offerChannels,
                };
            });
        }, 100);
        toggleTimersRef.current.add(timer);
    };

    const closeAllAccessModal = () => {
        toggleTimersRef.current.forEach(clearTimeout);
        toggleTimersRef.current.clear();
        setStartAnimation(false);
        setFadeOut(false);
        setSelection(baseSelection());
        setAllAccessModalOpen(false);
    };

    useEffect(() => {
        if (Object.keys(selection.selectedItems).length === 3) {
            const timer = setTimeout(() => setAllAccessModalOpen(true), 600);
            return () => clearTimeout(timer);
        }
    }, [selection.selectedItems]);

    return {
        ...selection,
        inheritedAllSelectedHref,
        allAccessModalOpen,
        startAnimation,
        fadeOut,
        fadeTiles,
        toggleSwitch,
        openAllAccessModal: () => setAllAccessModalOpen(true),
        closeAllAccessModal,
        resetTileFade: () => setFadeTiles(false),
    };
}
